/**
 * Utility functions for formatting dates/times in PST/PDT timezone
 * All times should be displayed in Pacific time regardless of user's location
 */

const PST_TIMEZONE = 'America/Los_Angeles';

/**
 * Format a date/time in PST/PDT timezone
 * @param {Date|string|number} date - The date to format
 * @param {object} options - Formatting options (same as Intl.DateTimeFormat)
 * @returns {string} Formatted date/time string
 */
export function formatInPST(date, options = {}) {
  if (!date) return '';
  
  const dateObj = date instanceof Date ? date : new Date(date);
  if (isNaN(dateObj.getTime())) return 'Invalid Date';
  
  const defaultOptions = {
    timeZone: PST_TIMEZONE,
    ...options,
  };
  
  return new Intl.DateTimeFormat('en-US', defaultOptions).format(dateObj);
}

/**
 * Format time in PST (12-hour format with AM/PM)
 * @param {Date|string|number} date - The date to format
 * @returns {string} Formatted time string (e.g., "3:30 PM")
 */
export function formatTimePST(date) {
  return formatInPST(date, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Format time in PST (24-hour format)
 * @param {Date|string|number} date - The date to format
 * @returns {string} Formatted time string (e.g., "15:30")
 */
export function formatTime24PST(date) {
  return formatInPST(date, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/**
 * Format date and time in PST
 * @param {Date|string|number} date - The date to format
 * @returns {string} Formatted date/time string
 */
export function formatDateTimePST(date) {
  return formatInPST(date, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Format full date and time in PST
 * @param {Date|string|number} date - The date to format
 * @returns {string} Formatted date/time string (e.g., "Monday, January 15, 2024 at 3:30 PM")
 */
export function formatFullDateTimePST(date) {
  return formatInPST(date, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Get date string in PST (YYYY-MM-DD format)
 * @param {Date|string|number} date - The date to format
 * @returns {string} Date string in YYYY-MM-DD format
 */
export function getDateStringPST(date) {
  if (!date) return '';
  
  const dateObj = date instanceof Date ? date : new Date(date);
  if (isNaN(dateObj.getTime())) return '';
  
  // Get date components in PST
  const year = formatInPST(dateObj, { year: 'numeric' });
  const month = formatInPST(dateObj, { month: '2-digit' });
  const day = formatInPST(dateObj, { day: '2-digit' });
  
  return `${year}-${month}-${day}`;
}

/**
 * Create a Date object from a date string and time string, treating them as PST
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @param {string} timeStr - Time string in HH:MM:SS format
 * @returns {Date} Date object representing the date/time in PST, converted to UTC
 */
export function createDateFromPST(dateStr, timeStr) {
  const [year, month, day] = dateStr.split('-').map(Number);
  const [hours, minutes, seconds = 0] = timeStr.split(':').map(Number);
  
  // Create date string in PST format: "2024-01-15T15:30:00"
  // Then append PST offset (-08:00 for PST, -07:00 for PDT)
  // We'll determine DST: roughly March-November is PDT
  const isDST = month >= 4 && month <= 10;
  const offsetHours = isDST ? -7 : -8;
  const offsetStr = `${offsetHours >= 0 ? '+' : ''}${String(offsetHours).padStart(2, '0')}:00`;
  
  return new Date(`${dateStr}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}${offsetStr}`);
}

